import React from 'react';
import moment from 'moment';
import { newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import { generateDataSourceMetricKey, analyseDataSourceMetricKey } from './dataSourcesMetrics';
import { SIMPLE_DATE_FORMAT, FLATTEN_METRICS_NAMES_MAPPING, ALL_PC_DEVICES, ALL_CONSOLE_DEVICES } from '../constants';
import { SortType } from '../enums';
import { ComposedDateMode } from '@/pages/intelligence/components/ComposedDatePicker/enums';
import type { IDateType, IDateValue } from '@/pages/intelligence/types/common';
import type {
  IPCConsoleGame,
  IPCConsoleGameMetricsData,
  IPCConsoleGamesMetricsCompareData,
  IPCConsoleGamesCompareTrendsResult,
  IPCConsoleGamesCumulativeMetric,
  IFilter,
} from '@/pages/intelligence/types/pcConsoleCompare';

type ITranslater = (payload: { id: string }, params?: { [key: string]: any }) => string;

export * from './regionSelect';

export function isNullOrUndefined(data?: any): boolean {
  return data === undefined || data === null;
}

export function isEmpty(data?: any): boolean {
  return isNullOrUndefined(data) || (typeof data === 'string' && data.trim().length === 0);
}

export function isArrayValueSame(arr1?: any[], arr2?: any[]): boolean {
  if (arr1?.length !== arr2?.length) return false;
  return !!arr1?.every((item1) => arr2?.includes(item1));
}

export function getSpecialArrayValue(values?: string[], optionsValues?: string[]): string[] {
  if (!Array.isArray(values)) return [];
  return isArrayValueSame(values, optionsValues) ? ['All'] : values;
}

export function generateKeyOfMetricsMap(entityId: string, period: string, metric: string): string {
  return `${entityId}-${period}-${metric}`;
}

export function generateKeyOfPeriodMetricsMap(period: string, metric: string): string {
  return `${period}-${metric}`;
}

export function createDateFormatter(mode: ComposedDateMode, period: IDateType): (value: any) => string {
  return (value) => {
    // 相对时间模式，不格式化x轴的文案
    if (mode === ComposedDateMode.Relative) return value;
    // 绝对时间模式下，并且为日粒度时间，不格式化
    if (period === 'date') return value;
    // 绝对时间模式下，并且为月粒度，格式化为YYYY-MM
    if (period === 'month') {
      const m = moment(value);
      return m.isValid() ? m.format('YYYY-MM') : value;
    }
    // 剩余情况 原样返回
    return value;
  };
}

export function metricValueFormatter(value: any): string {
  return `${newMathFormat(Number.parseFloat(value))}`;
}

export function metricGameSerieKeyFormatter(value: any, cfg: { translater: ITranslater }): string {
  const translater = cfg?.translater;
  const metricsKeys = Object.keys(FLATTEN_METRICS_NAMES_MAPPING);
  let result = value;
  for (const metricKey of metricsKeys) {
    const metricKeyReg = new RegExp(`^${metricKey}_`);
    if (!metricKeyReg.test(result)) continue;
    const metricNameLocaleKey = FLATTEN_METRICS_NAMES_MAPPING[metricKey];
    const metricName = translater?.({ id: metricNameLocaleKey });
    result = result.replace(metricKeyReg, `${metricName}_`);
    return result;
  }
  return result;
}

export function getGameIdByEntityGamePath(entity: IPCConsoleGame, entityType: 'pc' | 'console' | 'combine'): string {
  let gameIdTemp = '';
  if (entityType === 'pc') {
    gameIdTemp = entity?.pcGameId;
  } else if (entityType === 'console') {
    gameIdTemp = entity?.consoleGameId;
  } else if(entityType === 'combine'){
    gameIdTemp = entity?.combined_id
  }
  return gameIdTemp || entity?.gameId;
}

export function getGameId(game?: IPCConsoleGameMetricsData): string {
  return game?.game_id || game?.pc_id || game?.console_id || '';
}

export function analyseGamesMetricsData(
  gamesMetricsData: IPCConsoleGameMetricsData[],
  config: {
    periods: string[];
    metrics: string[];
  },
): IPCConsoleGamesMetricsCompareData {
  const { periods = [], metrics = [] } = config || {};
  const result: IPCConsoleGamesMetricsCompareData = {
    // 实体数据，即游戏
    entities: [],
    // 指标map，存储的对象，包含指标的全部数据
    metricsMap: {},
    // 各个阶段、各个指标的最小值，暂时没用上，后面有需求可以用上
    periodMinMetricsMap: {},
    // 各个阶段、各个指标的最大值
    periodMaxMetricsMap: {},
  };

  if (!Array.isArray(gamesMetricsData)) return result;
  if (!Array.isArray(periods)) return result;
  if (!Array.isArray(metrics)) return result;

  // 遍历游戏
  for (const gameMetricsData of gamesMetricsData) {
    const gameId = getGameId(gameMetricsData);
    if (!gameId) continue;
    result.entities.push({
      ...gameMetricsData,
      entityType: gameMetricsData.entity_type,
      gameId,
      pcGameId: gameMetricsData.pc_id,
      consoleGameId: gameMetricsData.console_id,
      gameName: gameMetricsData.name,
      cover: gameMetricsData.cover || gameMetricsData.console_cover,
      pcCover: gameMetricsData.cover,
      consoleCover: gameMetricsData.console_cover,
      publisher: gameMetricsData.publisher,
      releaseTime: gameMetricsData.release_time,
      pcReleaseTime: gameMetricsData.pc_release_time,
      consoleReleaseTime: gameMetricsData.console_release_time,
    });
    // 遍历游戏的时间段
    for (const period of periods) {
      if (!period) continue;
      const gamePeriod = gameMetricsData?.metric_list?.find((item) => item?.period === period);
      if (!gamePeriod) continue;
      const gamePeriodDataSource = gamePeriod?.source;
      const gamePeriodMetrics = gamePeriod?.detail;
      if (!gamePeriodDataSource) continue;
      if (!Array.isArray(gamePeriodMetrics)) continue;
      // 遍历当前游戏、当前时间段下的指标
      for (const metric of metrics) {
        if (!metric) continue;
        const gamePeriodMetric = gamePeriodMetrics?.find((item) => {
          const dataSourceMetricKey = generateDataSourceMetricKey(gamePeriodDataSource, item?.metric);
          return dataSourceMetricKey === metric;
        });
        if (!gamePeriodMetric) continue;

        /* 更新result.periodMaxMetricsMap中 保存各个阶段的最大值指标 */
        const periodMetrickey = generateKeyOfPeriodMetricsMap(period, metric);
        const currentPeriodMetricMaxMetric = result.periodMaxMetricsMap[periodMetrickey];
        const metricValue = Number.parseFloat(gamePeriodMetric?.value);
        const isMetricValueValid = !Number.isNaN(metricValue);
        if (
          isMetricValueValid &&
          (!currentPeriodMetricMaxMetric || metricValue > Number.parseFloat(currentPeriodMetricMaxMetric?.value))
        ) {
          result.periodMaxMetricsMap[periodMetrickey] = {
            ...gamePeriodMetric,
            metric,
            entityId: gameId,
            period,
          };
        }
        /* 更新result.periodMaxMetricsMap中 保存各个阶段的最大值指标 */

        /* 把当前指标 存储到metricsMap中，方便后面获取指标数据 */
        const metricsMapKey = generateKeyOfMetricsMap(gameId, period, metric);
        if (!result.metricsMap[metricsMapKey]) {
          result.metricsMap[metricsMapKey] = {
            ...gamePeriodMetric,
            entityId: gameId,
            metric,
            period,
          };
        }
        /* 把当前指标 存储到metricsMap中，方便后面获取指标数据 */
      }
    }
  }

  return result;
}

function sortByAbsoluteDate(dates: string[], dateSortType: SortType) {
  if (!Array.isArray(dates)) return;
  return dates.sort((a, b) => {
    const aDateObj = moment(a);
    const bDateObj = moment(b);
    if (!aDateObj.isValid()) return 1;
    if (!bDateObj.isValid()) return -1;
    if (dateSortType === SortType.Asc) return aDateObj.isAfter(bDateObj) ? 1 : -1;
    return aDateObj.isBefore(bDateObj) ? 1 : -1;
  });
}

function sortByRelativeDate(dates: string[], dateSortType: SortType) {
  if (!Array.isArray(dates)) return;
  return dates.sort((a, b) => {
    const aNum = Number.parseInt(a?.substring(1), 10);
    const bNum = Number.parseInt(b?.substring(1), 10);
    if (Number.isNaN(aNum) && Number.isNaN(bNum)) return 0;
    if (Number.isNaN(aNum)) return 1;
    if (Number.isNaN(bNum)) return -1;
    return dateSortType === SortType.Asc ? aNum - bNum : bNum - aNum;
  });
}

export function analyseGamesMetricsTrendsData(
  gamesMetricsTrendsData: IPCConsoleGamesCompareTrendsResult[],
  config?: {
    metrics: string[];
    mode: ComposedDateMode;
    dateSortType?: SortType;
  },
): {
  dimensions: string[];
  xAxisData: string[];
  sources: any[];
  cumulativeMetricsDataMap: {
    [key: string]: IPCConsoleGamesCumulativeMetric[];
  };
} {
  const { metrics = [], mode, dateSortType = SortType.Asc } = config || {};

  const result: {
    // 折线图 dimensions配置
    dimensions: string[];
    // 折线图 xAxisData配置，即x轴数据
    xAxisData: string[];
    // 折线图数据源
    sources: any[];
    // 指标累计值map，游戏id、指标 作为key存储
    cumulativeMetricsDataMap: {
      [key: string]: IPCConsoleGamesCumulativeMetric[];
    };
  } = {
    dimensions: [],
    xAxisData: [],
    sources: [],
    cumulativeMetricsDataMap: {},
  };

  const dimensionsMap: {
    [key: string]: boolean;
  } = {};

  const xAxisDataMap: {
    [key: string]: boolean;
  } = {};

  const periodFieldName = mode === ComposedDateMode.Relative ? 'launch_date' : 'time';
  const periodSortter = mode === ComposedDateMode.Relative ? sortByRelativeDate : sortByAbsoluteDate;

  if (!Array.isArray(metrics)) return result;

  // 遍历游戏
  for (const gamesMetricsTrendData of gamesMetricsTrendsData) {
    const gameId = gamesMetricsTrendData?.game_id;
    if (!gameId) continue;
    result.cumulativeMetricsDataMap[gameId] = gamesMetricsTrendData?.total || [];
    const histories = gamesMetricsTrendData?.history;
    if (!histories?.length) continue;
    // 遍历当前游戏下的 时间线数据
    for (const history of histories) {
      const date = history?.[periodFieldName];
      if (!xAxisDataMap?.[date]) {
        result.xAxisData.push(date);
        xAxisDataMap[date] = true;
      }
      let source = result.sources.find((item) => item?.date === date);
      if (!source) {
        source = {
          date,
        };
        result.sources.push(source);
      }
      const gameMetricsObjs = history?.metric_value_list;
      // 遍历当前游戏 当前时间线下的指标
      for (const metric of metrics) {
        if (!metric) continue;
        const { metricKey } = analyseDataSourceMetricKey(metric);
        const gameMetricObj = gameMetricsObjs?.find((item) => item?.metric === metricKey);
        if (!gameMetricObj) continue;
        const metricValue = gameMetricObj?.value;
        const dimensionKey = `${metric}_${gameId}`;
        source[dimensionKey] = metricValue;
        if (!dimensionsMap?.[dimensionKey]) {
          result.dimensions.push(dimensionKey);
          dimensionsMap[dimensionKey] = true;
        }
      }
    }
  }

  result.dimensions.sort((a, b) => {
    const aIndex = metrics?.findIndex((metric) => a?.startsWith(metric));
    const bIndex = metrics?.findIndex((metric) => b?.startsWith(metric));
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    if (aIndex === -1 && bIndex === -1) return 0;
    return aIndex - bIndex;
  });
  result.dimensions.unshift('date');

  periodSortter(result.xAxisData, dateSortType);

  return result;
}

/**
 * 根据勾选devices获取游戏的releaseTime，pc就用pc_release_time，console就用console_release_time，同时勾选pc、console，就用最早那个时间
 * @param game 游戏对象
 * @param devices 勾选的devices
 * @returns 该游戏的release time
 */
export function getReleaseTimeByDevices(game: IPCConsoleGame, devices?: string[]): string {
  const pcReleaseTime = game?.pcReleaseTime;
  const consoleReleaseTime = game?.consoleReleaseTime;
  const isPCDevices = devices?.some((device) => ALL_PC_DEVICES.includes(device));
  const isConsoleDevices = devices?.some((device) => ALL_CONSOLE_DEVICES.includes(device));
  const isConbinationDevices = isPCDevices && isConsoleDevices;
  // 只勾选了PC的device，用pc_release_time
  if (!isConbinationDevices && isPCDevices) return pcReleaseTime;
  // 只勾选了console的device，用console_release_time
  if (!isConbinationDevices && isConsoleDevices) return consoleReleaseTime;
  // 同时勾选pc、console，并且有一个release_time 没有数据，就返回有数据那个release_time
  if (!pcReleaseTime || !consoleReleaseTime) return pcReleaseTime || consoleReleaseTime;
  const pcReleaseTimeM = moment(pcReleaseTime);
  const consoleReleaseTimeM = moment(consoleReleaseTime);
  // pc、console的release_time都有数据，那就返回最早那个release_time
  return pcReleaseTimeM.isBefore(consoleReleaseTimeM) ? pcReleaseTime : consoleReleaseTime;
}

/**
 * 获取游戏的最早发行时间
 * @param game 游戏
 * @returns 游戏的最早发行时间
 */
export function getEarlistedReleaseTime(game: IPCConsoleGame): string {
  const pcReleaseTime = game?.pcReleaseTime;
  const consoleReleaseTime = game?.consoleReleaseTime;
  if (!pcReleaseTime) return consoleReleaseTime;
  if (!consoleReleaseTime) return pcReleaseTime;
  const pcReleaseTimeM = moment(pcReleaseTime);
  const consoleReleaseTimeM = moment(consoleReleaseTime);
  // pc、console的release_time都有数据，那就返回最早那个release_time
  return pcReleaseTimeM.isValid() && pcReleaseTimeM.isBefore(consoleReleaseTimeM) ? pcReleaseTime : consoleReleaseTime;
}

export const getReleaseTime = (time: string) => {
  if (!time) return '';
  const dateM = moment(time);
  return dateM.isValid() ? dateM.format('YYYY-MM-DD') : time;

  // if (time) {
  //   return moment(time).format('YYYY-MM-DD');
  // } else {
  //   return '';
  // }
}

const MOMENT_DATE_TYPES: {
  [key: string]: any;
} = {
  date: 'days',
  week: 'weeks',
  month: 'months',
};
export function getDateTimeByRelativeDateTime(params: {
  dateType: IDateType;
  dateValue: IDateValue;
  releaseTime?: string;
}): {
  startDate: string;
  endDate: string;
} {
  const { dateType, releaseTime, dateValue } = params || {};
  if (!dateType || !dateValue?.[0] || !dateValue?.[1])
    return {
      startDate: '',
      endDate: '',
    };
  const momentDateType = MOMENT_DATE_TYPES[dateType] || MOMENT_DATE_TYPES.date;
  const startDateNum = Number.parseInt(dateValue[0], 10);
  const endDateNum = Number.parseInt(dateValue[1], 10);
  /**
   * 若游戏自身有发布时间，直接取游戏自身的发布时间
   * 否则，取当前时间 往前推 一个时间段（相对时间的结束时间）作为发布时间
   */
  const releaseTimeTemp = releaseTime?.trim() ? moment(releaseTime) : moment().subtract(endDateNum - 1, momentDateType);

  let startDate = null;
  let endDate = null;

  startDate = moment(releaseTimeTemp).add(startDateNum - 1, momentDateType);
  // 保证开始时间 不超过当前时间
  startDate = moment.min(startDate, moment());

  endDate = moment(releaseTimeTemp).add(endDateNum - 1, momentDateType);
  // 保证结束时间 不超过当前时间
  endDate = moment.min(endDate, moment());

  if (dateType === 'month') {
    startDate.set('date', 1);
    endDate.set('date', 1);
  }

  return {
    startDate: !startDate?.isValid() ? '' : startDate.format(SIMPLE_DATE_FORMAT),
    endDate: !endDate?.isValid() ? '' : endDate.format(SIMPLE_DATE_FORMAT),
  };
}

export function selectAll(option: {
  key: keyof IFilter;
  allValues: string[];
  filter: IFilter;
  setFilter: React.Dispatch<React.SetStateAction<IFilter>>;
}) {
  const { key, allValues, filter, setFilter } = option || {};
  if (!key) return;
  if (!Array.isArray(allValues)) return;
  let valueTemp = filter?.[key];
  if (Array.isArray(valueTemp) && valueTemp.length > 0) return;
  valueTemp = [...allValues];
  const filterTemp = {
    ...filter,
    [key]: valueTemp,
  };
  setFilter?.(filterTemp);
}

export const addFormatMessageSafelyHandler =
  (translater: ITranslater): ITranslater =>
    (payload, params) => {
      let result = '';
      try {
        result = translater(payload, params);
      } catch (exception) {
        console.warn(exception);
        result = payload?.id;
      }
      return result ?? '';
    };
